import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import cc from 'classcat';
import { useSelector } from 'react-redux';
import { dispatch } from '../../store';
import {
    INavigationItem,
    selectMobileSideBar,
    updateMobileSideBarOpen,
    updateShowDropdown,
    closeAllDropdowns,
} from '../../store/slices/navigation/navigation.slice';
import { useTranslation } from '../../hooks/useTranslation';
import { Translation } from '../../commonTypes/translation.types';

interface INavigationMenuItemProps extends INavigationItem {
    collapsed: boolean;
}

const NavigationMenuItem = ({
    keyTitle,
    url,
    icon,
    activeIcon,
    iconAlt,
    subMenuItems,
    hasParams,
    disabled,
    collapsed,
    showDropdown,
}: INavigationMenuItemProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const mobileMenuOpen = useSelector(selectMobileSideBar);

    const [isActive, setIsActive] = useState<boolean>(false);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const [shouldRender, setShouldRender] = useState<boolean>(showDropdown ?? false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (url) checkSelected(url, hasParams);

        if (collapsed && subMenuItems.length > 0) {
            const active = subMenuItems?.find(x => checkSelected(x.url, x.hasParams));
            if (active) setIsActive(true);
            else setIsActive(false);

            dispatch(updateShowDropdown({ keyTitle, showDropdown: false }));
        }
    }, [location.pathname]);

    useEffect(() => {
        if (collapsed && showDropdown) {
            setShouldRender(false);
            setIsAnimating(false);
        }
    }, [collapsed]);

    useEffect(() => {
        if (showDropdown) {
            setShouldRender(true);
            setIsAnimating(true);
        } else if (shouldRender) {
            setIsAnimating(false);
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [showDropdown]);

    const clickHandler = () => {
        if (subMenuItems.length > 0) dispatch(updateShowDropdown({ keyTitle, showDropdown: !showDropdown }));
        else if (url) {
            dispatch(closeAllDropdowns());
            mobileMenuOpen && dispatch(updateMobileSideBarOpen(false));
            navigate(url);
        }
    };

    const checkSelected = (itemUrl: string, hasParams: boolean) => {
        if (subMenuItems && subMenuItems.length > 0) {
            for (let subItem of subMenuItems) if (subItem.url === location.pathname) return true;
        } else if (itemUrl) {
            if (hasParams && location.pathname.startsWith(itemUrl)) return true;
            return itemUrl === location.pathname;
        }
    };

    const checkSelectedSubItem = (itemUrl: string, hasParams: boolean) => {
        if (hasParams && location.pathname.startsWith(itemUrl)) return true;
        return itemUrl === location.pathname;
    };

    return (
        <div className={cc(['menu-item', collapsed && 'menu-item__collapsed'])}>
            <button
                ref={buttonRef}
                disabled={disabled}
                className={cc([
                    'menu-item__button',
                    { 'menu-item--active': isActive || showDropdown },
                    { 'menu-item--opened': checkSelected(url, hasParams) },
                    { 'menu-item--disabled': disabled },
                ])}
                onClick={clickHandler}
            >
                <div className="menu-item__label-icon">
                    <img src={checkSelected(url, hasParams) || isActive || showDropdown ? activeIcon : icon} alt={iconAlt} />
                    <p
                        className={`${
                            checkSelected(url, hasParams) || isActive || showDropdown ? 'menu-item__label--active' : 'menu-item__label'
                        } paragraph-4`}
                    >
                        {t(`navigation.${keyTitle}` as keyof Translation)}
                    </p>
                </div>
                {subMenuItems.length > 0 && (
                    <img
                        className={cc(['menu-item__dropdown--icon', { 'menu-item__dropdown--icon--up': showDropdown }])}
                        src="/svg/sidebar/arrow-down.svg"
                        alt="Dropdown down Arrow"
                    />
                )}
            </button>
            {shouldRender && (
                <>
                    <div
                        className={cc([
                            'menu-item__dropdown',
                            { 'menu-item__dropdown--entering': isAnimating && !collapsed },
                            { 'menu-item__dropdown--exiting': !isAnimating && !collapsed },
                        ])}
                        style={{ top: buttonRef.current?.getBoundingClientRect().top }}
                    >
                        <ul className="menu-item__dropdown--list">
                            {subMenuItems.map(item => (
                                <li key={item.url}>
                                    <button
                                        className={cc([
                                            'menu-item__list-item',
                                            checkSelectedSubItem(item.url, item.hasParams) && 'menu-item__list-item--active',
                                            item.disabled && 'menu-item__list-item--disabled',
                                        ])}
                                        onClick={() => {
                                            if (!item.disabled) {
                                                mobileMenuOpen && dispatch(updateMobileSideBarOpen(false));
                                                navigate(item.url);
                                            }
                                        }}
                                    >
                                        <p
                                            className={cc([
                                                'menu-item__list-item-label',
                                                item.disabled && 'menu-item__list-item-label--disabled',
                                                checkSelectedSubItem(item.url, item.hasParams) && 'menu-item__list-item-label--active',
                                            ])}
                                        >
                                            {t(`navigation.${item.keyTitle}` as keyof Translation)}
                                        </p>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {collapsed && (
                        <div
                            className="menu-item__event-bubbling-mask"
                            onClick={() => dispatch(updateShowDropdown({ keyTitle, showDropdown: false }))}
                        ></div>
                    )}
                </>
            )}
        </div>
    );
};

export default NavigationMenuItem;
